// Helper functions used by comparison view and its components
import { get } from '@/helpers/global-helpers'

/**
 * Determine whether a stat item (category or tag) has any data
 * in the provided survey results structure.
 *
 * The expected structure of `resultsForSurvey` is:
 * {
 *   [groupName]: {
 *     [segmentName]: {
 *       categories?: { [categoryName]: any },
 *       tags?: { [tagName]: any }
 *     }
 *   }
 * }
 *
 * @param {Object} params
 * @param {Object} params.resultsForSurvey - Results keyed by group and segment for one survey
 * @param {('categories'|'tags')} params.type - Which stat type to check
 * @param {string} params.name - The category or tag name to look for
 * @returns {boolean}
 */
export const statItemHasData = ({ resultsForSurvey, type, name }) => {
  if (!resultsForSurvey || !type || !name) return false

  return Object.keys(resultsForSurvey).some((groupName) => {
    return Object.keys(resultsForSurvey[groupName]).some((attributeName) => {
      if (attributeName === 'all') {
        const res = get(
          resultsForSurvey,
          `${groupName}.${attributeName}.${type}.${name}`
        )
        return res?.hasOwnProperty('value')
      } else {
        return Object.keys(resultsForSurvey[groupName][attributeName]).some(
          (segmentName) => {
            const res = get(
              resultsForSurvey,
              `${groupName}.${attributeName}.${segmentName}.${type}.${name}`
            )
            return res?.hasOwnProperty('value')
          }
        )
      }
    })
  })
}

/**
 * Merge newly loaded store data into an existing `selectedStatItem`.
 * Used by Comparison.vue watchers for categories/tags/questions store slices.
 *
 * @param {Object} params
 * @param {Object} params.selectedStatItem - Current selected stat item object
 * @param {('categoriesResultsPerPerson'|'tagsResultsPerPerson'|'questions')} params.target - Target slice to merge
 * @param {Object} params.newObj - New store object
 * @param {Object} params.oldObj - Previous store object
 * @returns {Object} Updated selectedStatItem (same reference if no changes)
 */
export const updateSelectedStatItemOnStoreChange = ({
  selectedStatItem,
  target,
  newObj,
  oldObj
}) => {
  if (!selectedStatItem || !newObj || !oldObj) return selectedStatItem
  const addedKeys = Object.keys(newObj).filter((k) => !oldObj[k])
  if (!addedKeys.length) return selectedStatItem

  if (target === 'questions') {
    const patch = addedKeys.reduce((res, key) => {
      res[key] = {
        ...(selectedStatItem.questions?.[key] || {}),
        ...newObj[key]
      }
      return res
    }, {})
    return {
      ...selectedStatItem,
      questions: {
        ...selectedStatItem.questions,
        ...patch
      }
    }
  }

  if (
    target === 'categoriesResultsPerPerson' ||
    target === 'tagsResultsPerPerson'
  ) {
    const patch = addedKeys.reduce((res, key) => {
      res[key] = newObj[key]
      return res
    }, {})
    return {
      ...selectedStatItem,
      [target]: {
        ...selectedStatItem[target],
        ...patch
      }
    }
  }

  return selectedStatItem
}

/**
 * Build stat item structures for a selected category or tag (and trends),
 * merging in per-person results and question details when available.
 *
 * @param {Object} params
 * @param {Object} params.resultsForSurveys - full store resultsForSurveys
 * @param {Object} params.allSurveyDetails - map of surveyName -> survey details
 * @param {Object} params.resultStoreCategoriesResultsPerPerson
 * @param {Object} params.resultStoreTagsResultsPerPerson
 * @param {Object} params.resultStoreQuestions
 * @param {string} params.surveyName
 * @param {string} params.segmentName - 'all' or attribute-segment token (e.g. 'gender-male-1')
 * @param {string} params.groupName
 * @param {('categories'|'tags'|'question')} params.statItemType
 * @param {string} params.statItemName
 * @returns {Object} statItems payload used by Comparison.vue
 */
export const getStatItemsForSelectedStatItem = ({
  resultsForSurveys,
  allSurveyDetails,
  resultStoreCategoriesResultsPerPerson,
  resultStoreTagsResultsPerPerson,
  resultStoreQuestions,
  surveyName,
  segmentName,
  groupName,
  statItemType,
  statItemName
}) => {
  let statItems = {}
  const attributeSegmentPath =
    segmentName !== 'all' ? segmentName.split('-').slice(0, 2).join('.') : 'all'
  const resultPath = `${surveyName}.${groupName}.${attributeSegmentPath}`
  const allResultsForGroupSegment = get(resultsForSurveys, resultPath, {})

  switch (statItemType) {
    case 'categories': {
      const surveyDetails = allSurveyDetails[surveyName]
      const categoryDetails = surveyDetails?.categories?.find(
        (c) => c.name === statItemName
      )
      if (!categoryDetails) {
        console.error(
          `Category ${statItemName} not found for survey ${surveyName}`
        )
        return {}
      }
      const questionNames = (categoryDetails.questions || []).map((q) => q.name)

      statItems = Object.keys(allResultsForGroupSegment).reduce((acc, key) => {
        if (key === 'categories' || key === 'trendCategories') {
          acc[key] = {
            [statItemName]: allResultsForGroupSegment[key]?.[statItemName]
          }
          if (resultStoreCategoriesResultsPerPerson?.[statItemName]) {
            acc['categoriesResultsPerPerson'] = {
              ...acc['categoriesResultsPerPerson'],
              [statItemName]:
                resultStoreCategoriesResultsPerPerson[statItemName]
            }
          }
          if (resultStoreTagsResultsPerPerson?.[statItemName]) {
            acc['tagsResultsPerPerson'] = {
              ...acc['tagsResultsPerPerson'],
              [statItemName]: resultStoreTagsResultsPerPerson[statItemName]
            }
          }
        } else if (key === 'questions' || key === 'trendQuestions') {
          acc[key] = questionNames.reduce((subAcc, questionName) => {
            subAcc[questionName] =
              allResultsForGroupSegment[key]?.[questionName]
            if (resultStoreQuestions?.[questionName]) {
              subAcc[questionName] = {
                ...subAcc[questionName],
                ...resultStoreQuestions[questionName]
              }
            }
            return subAcc
          }, {})
        } else if (key === 'tags' || key === 'trendTags') {
          acc[key] = allResultsForGroupSegment[key]
        }
        return acc
      }, {})
      break
    }
    case 'tags': {
      statItems = {
        tags: {
          [statItemName]: allResultsForGroupSegment?.tags?.[statItemName]
        },
        trendTags: {
          [statItemName]: allResultsForGroupSegment?.trendTags?.[statItemName]
        }
      }
      break
    }
    case 'question': {
      // Reserved for potential future use.
      break
    }
    default:
      console.warn(`Unhandled statItemType: ${statItemType}`)
      return {}
  }

  return statItems
}
